<template>
  <NavPanelLink
    v-if="shouldShowChat"
    :active="chatSidebarOpen"
    :class="{[$style.navExpanded]: expanded}"
    :disabled="isChatLoading"
    isButton
    :item="chatItem"
    :panelExpanded="expanded"
    @click="toggleChatApp"
  >
    <template v-slot:default>
      <DaysRemainingBadge
        :class="$style.remainingBadge"
        :comparisonDate="getOrgCreatedDate"
        :expanded="expanded"
        :trialLength="trialLength"
      />
      <div
        v-show="hasNewMessage"
        :class="{
          [$style.newMessage]: true,
          [$style.newMessageExpanded]: expanded }"
        title="new message"
      />
    </template>
    <template v-slot:popover>
      <NavPopover
        v-if="showPopover"
        arrowPlacement="bottom"
        :bodyText="popoverBodyText"
        buttonText="Let's Chat"
        :class="{
          [$style.popover]: true,
          [$style.popoverExpanded]: expanded,
        }"
        :handleAction="handleOpenChatPopover"
        :handleClose="handleClose"
        :headerText="popoverHeaderText"
        isButton
        placement="right-start"
      />
    </template>
  </NavPanelLink>
</template>

<script>
import { ChatConsts } from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import DateUtil from 'client/util/DateUtil';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import LoadingIcon from 'components/LoadingOverlay/LoadingIcon.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import MspState from 'client/components/models/MspState';
import NavPanelLink from 'client/components/NavPanel/NavPanelLink.vue';
import NavPanelLinkListItem from 'client/components/NavPanel/NavPanelLinkListItem';
import NavPopover from 'client/components/NavPanel/NavPopover.vue';
import iconChat from 'public/img/icons/nav/icon-nav-chat.svg?inline';

import DaysRemainingBadge from './DaysRemainingBadge.vue';

const { isAnMsp } = MspState;
const {
  delayTime,
  popoverBodyText,
  popoverHeaderText,
  trialLength,
} = ChatConsts;
const chatModule = 'ChatModule';
const growthModule = 'GrowthModule';
const onboardingModule = 'OnboardingModule';

const CHAT_INTERACTION_ID = 135875; // the id for the drift playbook
const CHAT_NAME = 'navChat';

export default {
  name: 'Chat',

  components: {
    DaysRemainingBadge,
    NavPopover,
    NavPanelLink,
  },

  props: {
    expanded: Boolean,

    imageComponent: {
      type: Object,
      default() {
        return iconChat;
      },
    },
  },

  data() {
    return {
      chatSidebarOpen: false,
      chatMessage: {},
      delayTime,
      popoverBodyText,
      popoverHeaderText,
      startedConversation: false,
      shouldShowChat: false,
      showPopover: false,
      trialLength,
    };
  },

  computed: {
    ...mapGetters('OrganizationsCollection', ['getOrgCreatedDate']),

    ...mapGetters(chatModule, {
      chatMsg: 'getChatMessage',
      conversationStarted: 'getConversationStarted',
      isChatLoading: 'getIsLoading',
      isChatOpen: 'getIsOpen',
      chatLoadingError: 'getHasLoadingError',
    }),

    ...mapGetters(onboardingModule, {
      onboardingFlowComplete: 'getOnboardingFlowComplete',
    }),

    ...mapState('SettingsModule', [
      'orgId',
      'user',
    ]),

    ...mapState(growthModule, [
      'isMobile',
    ]),

    ...mapState(onboardingModule, [
      'adminGrowthData',
      'showProductTour',
    ]),

    chatItem() {
      return new NavPanelLinkListItem({
        imageComponent: this.isChatLoading ? LoadingIcon : iconChat,
        title: 'Live Chat',
      });
    },

    chatSeen() {
      return this.adminGrowthData.onboardingState.chatSeen;
    },

    hasNewMessage() {
      return !this.chatSidebarOpen && !!this.chatMessage.conversationId;
    },
  },

  watch: {
    chatLoadingError(newValue) {
      if (newValue === true) {
        this.updateShowError();
      }
    },

    chatMsg(newValue) {
      this.updateChatNewMessage(newValue);
    },

    conversationStarted() {
      this.updateChatSeen(true);
    },

    expanded() {
      this.updateNavExpanded();
    },

    isChatOpen(newValue) {
      if (newValue) {
        this.updateNavExpanded();
      }
    },

    onboardingFlowComplete(newValue) {
      if (newValue === true) {
        this.autoOpenPopover();
      }
    },
  },

  created() {
    this.shouldShowChat = (!isAnMsp
    && !!this.orgId
    && !!this.getOrgCreatedDate
    && DateUtil.withinDayRangeOfToday(this.getOrgCreatedDate, this.trialLength));
  },

  mounted() {
    if (window && this.shouldShowChat) {
      const { email, firstname, lastname } = this.user;
      this.loadScript();
      this.updateOrg(this.orgId);
      this.updateUserSettings({ email, firstname, lastname });
    }

    if (this.shouldShowChat) this.autoOpenPopover();
  },

  methods: {
    ...mapActions(chatModule, [
      'loadScript',
      'toggleChat',
      'updateOrg',
      'updateUserSettings',
    ]),

    ...mapActions(onboardingModule, [
      'updateChatSeen',
    ]),

    autoOpenPopover() {
      // Opens Chat Popover After 2 minutes, if chat isn't open
      // and chat was not seen before
      const shouldOpenNewChat = !this.chatSeen
      && !this.isChatOpen
      && !this.conversationStarted
      && !this.isMobile
      && !this.showProductTour
      && this.onboardingFlowComplete;

      if (shouldOpenNewChat) {
        this.clearDelayOpening();
        this.delayOpening = setTimeout(this.openPopover, this.delayTime);
      }
    },

    clearDelayOpening() {
      if (this.delayOpening) clearTimeout(this.delayOpening);
    },

    handleClose(location) {
      this.showPopover = false;
      this.updateChatSeen(true);
      EventHelper.triggerChatPopoverDismissed({
        location,
      });
    },

    handleOpenChatPopover() {
      this.updateChatSeen(true);
      EventHelper.triggerChatPopoverCtaClicked();
      this.toggleChatApp();
    },

    openPopover() {
      this.showPopover = true;
    },

    toggleChatApp() {
      this.clearDelayOpening();
      this.showPopover = false;

      this.toggleChat({
        interactionId: CHAT_INTERACTION_ID,
        chatName: CHAT_NAME,
      });
    },

    updateChatNewMessage(message) {
      this.chatMessage = message;
    },

    updateChatSidebar(chatSidebarOpen) {
      this.chatSidebarOpen = chatSidebarOpen;
    },

    updateNavExpanded() {
      const driftWidget = document.getElementsByClassName('jc-navChat')[0];
      const expandedClass = 'expanded';

      if (driftWidget && driftWidget.classList) {
        if (this.expanded) {
          driftWidget.classList.add(expandedClass);
        } else {
          driftWidget.classList.remove(expandedClass);
        }
      }
    },

    updateShowError() {
      this.updateChatSeen();
      if (!this.isChatOpen) {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            cancelText: '',
            continueText: 'ok, got it',
            headerText: 'It looks like you’re using a content blocker!',
            mdTemplate: 'We’ve detected that you’re using a content blocking plugin for your browser.\n\nTo access Live Chat, go to your content blocking plugin and add jumpcloud.com as an exception.',
          },
        });
      }
    },
  },
};
</script>

<style module>
@value jcError from "~css/brand-colors.css";
@value (navWidth, navWidthExpanded) from '~css/layout.css';
@value (max-width-large) from '~css/breakpoints.css';
@value (tooltipZIndex) from '~css/z-index.css';
@value (panelSpeedSm, panelTimingFunction) from '~css/transitions.css';
@value newMessageSize: 4px;

.newMessage {
  background-color: jcError;
  border-radius: newMessageSize;
  height: newMessageSize;
  left: 32px;
  position: absolute;
  top: 8px;
  transition: top 0.4s linear;
  width: newMessageSize;
}

.popover {
  height: 168px;
  left: 112px;
  margin-top: -240px;
  padding: 1rem;
  position: fixed;
  top: auto;
  width: 168px;
  z-index: tooltipZIndex;
}

.popoverExpanded {
  left: 275px;
}

.remainingBadge {
  opacity: 0;
  position: absolute;
  right: var(--navPanelPadding);
  top: 2px;
  transition: all panelSpeedSm panelTimingFunction;
}

.navExpanded .remainingBadge {
  opacity: 1;
}

/* stylelint-disable */
:global(.drift-frame-chat.jc-navChat) {
  bottom: 0 !important;
  left: navWidth !important;
  right: auto !important;
  transition: left panelSpeedSm panelTimingFunction !important;
}

:global(.expanded.drift-frame-chat.jc-navChat) {
  left: navWidthExpanded !important;
}

@media (max-width: max-width-large) {
  :global(.expanded.drift-frame-chat.jc-navChat) {
    left: navWidth !important;
  }

  .navExpanded .remainingBadge {
    opacity: 0;
  }

  .popoverExpanded {
    left: 8rem;
  }
}

/* Hide the Drift Widget in various states we don't want */

/* And yes we do have to go unclear on it */
:global(div.drift-frame-controller) {
  display: none !important;
}

:global(.drift-widget-hidden[style]),
:global(.drift-widget-welcome-online[style]),
:global(.drift-widget-welcome-expanded-online[style]),
:global(.drift-widget-welcome-away[style]),
:global(.drift-widget-welcome-expanded-away[style]),
:global(.drift-widget-slider[style]) {
  display: none !important;
}
</style>
